(() => {
  if (customElements.get('demo-request-form')) return;

  // Share one script across product sections; allow another attempt after a load error.
  let turnstilePromise;
  let turnstileLoadId = 0;
  function loadTurnstile() {
    if (window.turnstile?.render) return Promise.resolve(window.turnstile);
    if (turnstilePromise) return turnstilePromise;
    turnstilePromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const callbackName = `demoTurnstileLoaded${++turnstileLoadId}`;
      let settled = false;
      const finish = (error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (error) {
          script.remove();
          turnstilePromise = null;
          reject(error);
        } else resolve(window.turnstile);
      };
      const timer = setTimeout(() => finish(new Error('verification_unavailable')), 15000);
      // The Turnstile onload callback supports async loading; ready() does not.
      window[callbackName] = () => {
        finish(window.turnstile?.render ? undefined : new Error('verification_unavailable'));
        delete window[callbackName];
      };
      script.src = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${callbackName}`;
      script.async = true;
      script.onerror = () => finish(new Error('verification_unavailable'));
      document.head.appendChild(script);
    });
    return turnstilePromise;
  }

  class DemoRequestForm extends HTMLElement {
    connectedCallback() {
      if (this.form) return;
      this.form = this.querySelector('form');
      this.button = this.querySelector('button[type="submit"]');
      this.label = this.button.querySelector('span');
      this.defaultLabel = this.label.textContent;
      this.status = this.querySelector('.demo-form__status');
      this.verification = this.querySelector('.demo-form__turnstile');
      this.verificationMessage = this.querySelector('.demo-form__verification-message');
      this.retryVerification = this.querySelector('.demo-form__verification-retry');
      this.session = {};
      this.completed = false;
      this.token = '';
      this.widgetId = null;
      this.verification.replaceChildren();
      this.verification.removeAttribute('data-interactive');
      this.onRetryVerification = () => this.resetVerification();
      this.retryVerification.addEventListener('click', this.onRetryVerification);
      this.endpoint = null;
      this.onSubmit = this.submit.bind(this);
      this.form.addEventListener('submit', this.onSubmit);
      this.onFieldEdit = event => {
        if (['firstName', 'lastName', 'email', 'country', 'consent'].includes(event.target.name)) this.resumeRequest();
      };
      this.form.addEventListener('input', this.onFieldEdit);
      this.form.addEventListener('change', this.onFieldEdit);
      try {
        const endpoint = new URL(this.dataset.endpoint);
        if (endpoint.protocol !== 'https:' || endpoint.username || endpoint.password) throw new Error();
        if (!/^\d+$/.test(this.dataset.productId)) throw new Error();
        this.endpoint = endpoint.href;
        this.button.disabled = false;
        this.initVerification();
      } catch {
        this.show('This demo form is not available yet. Please contact us for help.', 'error');
      }
    }

    disconnectedCallback() {
      this.session = null;
      this.controller?.abort();
      this.controller = null;
      this.pending = false;
      this.token = '';
      if (this.widgetId !== null) this.turnstile?.remove(this.widgetId);
      this.widgetId = null;
      this.retryVerification?.removeEventListener('click', this.onRetryVerification);
      this.form?.removeEventListener('submit', this.onSubmit);
      this.form?.removeEventListener('input', this.onFieldEdit);
      this.form?.removeEventListener('change', this.onFieldEdit);
      this.form = null;
    }

    verificationNotice(message = '', retry = false) {
      this.verificationMessage.textContent = message;
      this.retryVerification.hidden = !retry;
    }

    completeRequest() {
      this.completed = true;
      // Invalidate callbacks from the removed widget, including delayed challenges.
      this.session = {};
      this.token = '';
      if (this.widgetId !== null) this.turnstile.remove(this.widgetId);
      this.widgetId = null;
      this.verification.replaceChildren();
      this.verification.removeAttribute('data-interactive');
      this.verificationNotice();
      this.form.reset();
    }

    resumeRequest() {
      if (!this.completed || this.pending) return;
      this.completed = false;
      this.show('', '');
      this.initVerification();
    }

    async initVerification() {
      const session = this.session;
      if (!session || session.initializing || this.completed) return;
      session.initializing = true;
      const current = () => this.session === session && this.isConnected;
      const failed = () => {
        if (!current()) return;
        this.token = '';
        this.verificationNotice('Security check could not complete. Please retry or refresh this page.', true);
      };
      try {
        this.turnstile = await loadTurnstile();
        if (!current()) return;
        this.widgetId = this.turnstile.render(this.verification, {
          sitekey: this.dataset.turnstileSitekey,
          action: 'demo_request',
          appearance: 'interaction-only',
          execution: 'render',
          theme: 'light',
          size: 'flexible',
          'response-field': false,
          callback: token => {
            if (!current()) return;
            this.token = token;
            this.tokenTime = Date.now();
            this.verification.removeAttribute('data-interactive');
            this.verificationNotice();
          },
          'before-interactive-callback': () => {
            if (!current()) return;
            this.verification.setAttribute('data-interactive', '');
            this.verificationNotice();
          },
          'expired-callback': () => {
            if (!current()) return;
            this.token = '';
            this.verificationNotice();
          },
          'error-callback': () => { failed(); return true; },
          'timeout-callback': failed,
          'unsupported-callback': failed,
        });
      } catch { failed(); }
      finally { session.initializing = false; }
    }

    resetVerification() {
      if (this.completed) return;
      this.token = '';
      this.verificationNotice();
      this.verification.removeAttribute('data-interactive');
      if (this.widgetId !== null) {
        try { this.turnstile.reset(this.widgetId); }
        catch { this.verificationNotice('Security check could not restart. Please refresh this page.', true); }
      } else this.initVerification();
    }

    show(message, state) {
      this.status.textContent = message;
      this.status.dataset.state = state;
    }

    async submit(event) {
      event.preventDefault();
      if (this.pending || !this.endpoint || !this.form.reportValidity()) return;
      this.resumeRequest();
      const fields = new FormData(this.form);
      const demoTag = (this.dataset.demoTag || '').trim();
      if (!demoTag || demoTag.length > 100 || /[\x00-\x1f\x7f]/.test(demoTag)) {
        this.show('A demo is not available for this program yet. Please contact us for help.', 'error');
        return;
      }
      const country = fields.get('country');
      if (!/^[A-Z]{2}$/.test(country || '') || ['RU', 'BY'].includes(country)) {
        this.show('Please select a country from the list.', 'error');
        return;
      }
      // Tokens are single-use and expire after five minutes. Never send without one.
      if (this.token && Date.now() - this.tokenTime >= 290000) this.resetVerification();
      if (!this.token) {
        if (this.widgetId === null) this.initVerification();
        return;
      }
      const turnstileToken = this.token;
      this.token = '';
      this.pending = true;
      this.button.disabled = true;
      this.form.setAttribute('aria-busy', 'true');
      this.label.textContent = this.dataset.loadingLabel;
      this.show('', '');
      const controller = new AbortController();
      this.controller = controller;
      const timeout = setTimeout(() => controller.abort(), 25000);
      try {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'omit',
          redirect: 'error',
          signal: controller.signal,
          body: JSON.stringify({
            email: fields.get('email').trim(),
            firstName: fields.get('firstName').trim(),
            lastName: fields.get('lastName').trim(),
            productId: this.dataset.productId,
            demoTag,
            country,
            consent: fields.get('consent') === 'on',
            consent_version: 'notice_v1',
            turnstileToken,
            website: fields.get('website') || '',
          }),
        });
        const result = await response.json();
        if (this.controller !== controller || !this.isConnected) return;
        if (response.ok && result.ok === true && ['request_accepted', 'already_requested'].includes(result.code)) {
          this.completeRequest();
          this.show(result.code === 'already_requested' ? this.dataset.repeatMessage : this.dataset.successMessage, 'success');
        } else {
          const messages = {
            consent_required: 'Please tick the checkbox to receive the demo, training tips and offers.',
            invalid_email: 'Please enter a valid email address.',
            invalid_name: 'Please enter your first and last name (up to 100 characters each).',
            invalid_country: 'Please select a country from the list.',
            unknown_product: 'A demo is not available for this program yet.',
            demo_not_configured: 'A demo is not available for this program yet. Please contact us for help.',
            invalid_demo_tag: 'A demo is not available for this program yet. Please contact us for help.',
            confirmation_required: 'Please confirm your email subscription using the confirmation email in your inbox, then try again.',
            resubscription_required: 'This email is unsubscribed. Please contact us for help with your demo.',
            subscription_required: 'This email cannot receive the demo yet. Please contact us for help.',
            address_unavailable: 'We cannot send to this email address. Please use another address.',
            rate_limited: 'Too many requests. Please try again later.',
            verification_required: 'Please complete the security check and try again.',
            verification_failed: 'Security verification expired or failed. Please complete the new check and try again.',
            verification_unavailable: 'Security verification is temporarily unavailable. Please try again shortly.',
          };
          this.show(messages[result.code] || 'We could not complete your request. Please try again later or contact us.', 'error');
        }
      } catch {
        if (this.controller === controller && this.isConnected) this.show('We could not confirm your request. Check your connection and try again. If it was accepted, another request will not resend this demo.', 'error');
      } finally {
        clearTimeout(timeout);
        // A section can be removed and reconnected while a request is in flight.
        if (this.controller === controller) {
          this.pending = false;
          this.button.disabled = !this.endpoint;
          this.label.textContent = this.defaultLabel;
          this.form?.removeAttribute('aria-busy');
          if (this.isConnected && !this.completed) this.resetVerification();
          if (this.isConnected) this.status.focus();
        }
      }
    }
  }

  customElements.define('demo-request-form', DemoRequestForm);
})();
